import type { Observer } from '@naturehouse/nh-essentials/lib/architecture/ObserverPattern';
import InitializationError from '@naturehouse/nh-essentials/lib/exceptions/InitializationError';
import SubjectMixin from '../../../../util/subjectMixin';
import HousePriceListCollection from '../../../../collections/price-list/HousePriceListCollection';
import type {
    PriceListCollectionApiData,
    PriceListPriceInformation
} from '../../../../collections/price-list/PriceListCollection.types';
import DetailPriceListModel from '../../../../models/price-list/DetailPriceListModel';
import renderPriceDetailsEvents from '../../../../events/renderPriceDetailEvents';
import { calculatePricePerNight } from '../../../../gtm/GTMHelpers';
import DialogHandler from '../../../app/components/DialogHandler';
import PricingListRenderer from './DetailPriceListRenderer';
import './DetailPriceList.pcss';

export default class DetailPriceList extends SubjectMixin(HTMLElement) {
    readonly #priceListCollection: HousePriceListCollection;

    readonly #form: HTMLFormElement | null = null;

    readonly #houseId: string | null = null;

    #extraCostsDialog: DialogHandler | null = null;

    #lastRenderEvent: CustomEvent<PriceListCollectionApiData> | null = null;

    #latestRenderNumber = 0;

    set loading(value: boolean) {
        this.classList.toggle('loading-spinner', value);
        this.toggleAttribute('loading', value);
    }

    public constructor() {
        super();

        const formId = this.getAttribute('form-id') ?? '';
        this.#form = document.getElementById(formId) as HTMLFormElement;
        this.#houseId = this.getAttribute('house-id');
        this.#priceListCollection = HousePriceListCollection.getInstance();
    }

    public disconnectedCallback(): void {
        HousePriceListCollection.destroyInstance();
    }

    public override attach(observer: Observer): void {
        super.attach(observer);

        if (this.#lastRenderEvent) {
            observer.update(this.#lastRenderEvent);
        }
    }

    public async render(): Promise<void> {
        if (this.#form === null || this.#houseId === null) {
            throw new InitializationError({
                detailForm: this.#form,
                houseId: this.#houseId
            });
        }

        this.clear();
        this.#lastRenderEvent = null;
        this.loading = true;
        this.#latestRenderNumber += 1;
        const renderNumber = this.#latestRenderNumber;

        const detailPriceListData = DetailPriceListModel.parseToDetailPriceListFormData(
            new FormData(this.#form)
        );

        if (
            !detailPriceListData['price_calculation[arrivalDate]'] ||
            !detailPriceListData['price_calculation[departureDate]']
        ) {
            this.loading = false;
            return;
        }

        const data = await this.#priceListCollection.retrieve<PriceListCollectionApiData>({
            houseId: this.#houseId,
            searchParams: DetailPriceListModel.parseToSearchParams(detailPriceListData)
        });

        if (renderNumber !== this.#latestRenderNumber) {
            return;
        }

        if (!data) {
            this.loading = false;
            return;
        }

        const priceInformation: PriceListPriceInformation = data.priceInformation;
        const promotionalDiscount: number = priceInformation.discount ?? 0;
        const { base, discount } = priceInformation.total;
        const nights = data.bookingInformation.numberOfNights ?? 1;

        const pricePerNight = calculatePricePerNight(priceInformation.basePrice, nights);

        const promotionalDiscountPerNight = calculatePricePerNight(promotionalDiscount, nights);

        const priceListHtml = await PricingListRenderer.render({
            promotionalDiscount: promotionalDiscount,
            totalStayAmount: discount,
            totalStayAmountWithoutDiscount: base,
            basePrice: priceInformation.basePrice,
            pricePerNight: pricePerNight,
            promotionalDiscountPerNight: promotionalDiscountPerNight
        });

        if (renderNumber !== this.#latestRenderNumber) {
            return;
        }

        this.innerHTML = priceListHtml;

        this.#extraCostsDialog = new DialogHandler({
            buttonSelector: '[data-role="extra-costs-dialog-toggle"]',
            dialogSelector: '[data-role="extra-costs-dialog"]'
        });
        this.#extraCostsDialog.initialize();
        this.loading = false;

        if (!this.#extraCostsDialog.dialog) {
            return;
        }

        this.#lastRenderEvent = new CustomEvent(renderPriceDetailsEvents.renderPriceDetails, {
            detail: data
        });
        this.notify(this.#lastRenderEvent);
    }

    public clear(): void {
        this.innerHTML = '';
        this.#extraCostsDialog?.destroy();
    }
}

if (!customElements.get('nh-detail-price-list')) {
    customElements.define('nh-detail-price-list', DetailPriceList);
}
